import { useContext, useRef, useCallback, useEffect } from 'react';
import { styled } from '@mui/system';
import SwitchUnstyled, { switchUnstyledClasses } from '@mui/core/SwitchUnstyled';

import { CardContext } from 'context/cards/cardContext';
import { HIDE_CARD_INFO } from 'context/cards/types';
import SnackBarComponent from 'components/shared/SnackBarComponent';
import { getCardInfo } from 'lib/api/cards';
import { SAVE_TOKENS } from 'context/cards/types';
import { LoaderContext } from 'components/shared/Loader/LoaderContext';

const Root = styled('span')(`
  font-size: 0;
  position: relative;
  display: inline-block;
  width: 32px;
  height: 20px;
  margin: 10px;
  cursor: pointer;

  &.${switchUnstyledClasses.disabled} {
    opacity: 0.4;
    cursor: not-allowed;
  }

  & .${switchUnstyledClasses.track} {
    background: #B3C3D3;
    border-radius: 10px;
    display: block;
    height: 100%;
    width: 100%;
    position: absolute;
  }

  & .${switchUnstyledClasses.thumb} {
    display: block;
    width: 14px;
    height: 14px;
    top: 3px;
    left: 3px;
    border-radius: 16px;
    background-color: #FFF;
    position: relative;
    transition: all 200ms ease;
  }

  &.${switchUnstyledClasses.focusVisible} .${switchUnstyledClasses.thumb} {
    background-color: rgba(255,255,255,1);
    box-shadow: 0 0 1px 8px rgba(0,0,0,0.25);
  }

  &.${switchUnstyledClasses.checked} { 
    .${switchUnstyledClasses.thumb} {
      left: 14px;
      top: 3px;
      background-color: #FFF;
    }

    .${switchUnstyledClasses.track} {
      background: #98CD6C;
    }
  }

  & .${switchUnstyledClasses.input} {
    cursor: inherit;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    opacity: 0;
    z-index: 1;
    margin: 0;
  }`);

const CVVSwitch = ({ card, checked, setChecked }) => {
    const { setIsLoading } = useContext(LoaderContext);
    const { dispatch } = useContext(CardContext);
    const snackBarRef = useRef();

    const fetchCardInfo = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getCardInfo(card.id);
            dispatch({
                type: SAVE_TOKENS,
                payload: {
                    [response.id]: {
                        id: response.id,
                        token: response.showToken,
                        vaultId: response.vaultId
                    }
                }
            });
        } catch (error) {
            snackBarRef.current.openSnackBar({
                label: error.response.data.message,
                severity: 'error'
            });
        }
        setIsLoading(false);
    }, [card.id, dispatch, setIsLoading]);

    useEffect(() => {
        if (checked) {
            fetchCardInfo();
        }
    }, [checked, fetchCardInfo]);

    const handleChange = ({ target }) => {
        if (card.solidData.cardStatus !== 'active') {
            snackBarRef.current.openSnackBar({
                label: 'Please activate your card.',
                severity: 'error'
            });
            return;
        }
        if (!target.checked) {
            dispatch({
                type: HIDE_CARD_INFO
            });
        }
        setChecked(target.checked);
    };

    return (
        <>
            <SwitchUnstyled component={Root} checked={checked} onChange={handleChange} />
            <SnackBarComponent ref={snackBarRef} />
        </>
    );
};

export default CVVSwitch;
